import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { FiUser, FiShoppingCart, FiSearch, FiMenu, FiX, FiHeart } from 'react-icons/fi';
import api from '../../services/api';

const Header = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { getTotalItems } = useCart();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [collections, setCollections] = useState([]);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch navigation collections from backend
  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await api.publicAPI.getCollections({ light: true });
        if (response.data.success && response.data.collections?.length > 0) {
          setCollections(response.data.collections);
        }
      } catch (error) {
        console.error('Error fetching navigation collections:', error);
      }
    };

    fetchCollections();
  }, []);

  const performSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;

    try {
      setSearchLoading(true);
      console.log('🔍 Searching for:', searchQuery.trim());
      
      const response = await api.publicAPI.searchProducts(searchQuery.trim());
      console.log('📦 Search response:', response.data);
      
      if (response.data.success && response.data.products) {
        const results = response.data.products.slice(0, 5); // Show only first 5 results
        console.log('✅ Search results:', results);
        setSearchResults(results);
      } else {
        console.log('⚠️ No search results found');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('❌ Search error:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (searchQuery.trim().length > 2) {
      const timeoutId = setTimeout(() => {
        performSearch();
      }, 300);

      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, performSearch]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    navigate('/');
  };

  // Create navigation from collections
  const navigation = collections.map(collection => ({
    name: collection.name,
    href: `/collections/${collection.slug || collection.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`
  }));

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      {/* Top Bar */}


      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-2">
          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-1.5 -ml-1 text-gray-700 hover:text-black shrink-0"
          >
            {isMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>

          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0 mr-auto lg:mr-0">
            <img
              src="/BeyondXL_in_Original-Black.png"
              alt="BeyondXL"
              className="h-7 sm:h-8 md:h-10 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="text-gray-700 hover:text-black font-medium transition-colors"
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Right side items */}
          <div className="flex items-center space-x-0.5 sm:space-x-2 lg:space-x-4 shrink-0">
            {/* Search */}
            <div className="relative" ref={searchRef}>
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="p-1.5 sm:p-2 text-gray-700 hover:text-black lg:hidden"
              >
                <FiSearch size={20} />
              </button>
              
              <div className={`${isSearchOpen ? 'block' : 'hidden'} lg:block absolute lg:relative top-full lg:top-auto left-0 lg:left-auto right-0 lg:right-auto mt-2 lg:mt-0 w-full lg:w-auto`}>
                <form onSubmit={handleSearchSubmit} className="relative">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full lg:w-80 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  
                  {/* Search Results Dropdown */}
                  {searchResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                      {searchLoading ? (
                        <div className="p-4 text-center text-gray-500">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black mx-auto"></div>
                        </div>
                      ) : (
                        <div className="max-h-96 overflow-y-auto">
                          {searchResults.map((product) => (
                            <Link
                              key={product.id}
                              to={`/products/${product.id}`}
                              onClick={() => {
                                setIsSearchOpen(false);
                                setSearchQuery('');
                                setSearchResults([]);
                              }}
                              className="flex items-center space-x-3 p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                            >
                              <img
                                src={api.utils.getImageUrl(product.images?.[0], { width: 1200, height: 1500, fit: 'cover' })}
                                alt={product.name}
                                className="w-12 aspect-[4/5] object-cover object-center rounded"
                                onError={(e) => {
                                  e.target.src = 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=100&h=100&fit=crop';
                                }}
                              />
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-gray-900 truncate">{product.name}</h4>
                                <p className="text-sm text-gray-500">{product.collection_name}</p>
                                <p className="text-sm font-semibold text-black">₹{product.price?.toLocaleString()}</p>
                              </div>
                            </Link>
                          ))}
                          <div className="p-3 border-t border-gray-200">
                            <Link
                              to={`/products?search=${encodeURIComponent(searchQuery)}`}
                              onClick={() => {
                                setIsSearchOpen(false);
                                setSearchQuery('');
                                setSearchResults([]);
                              }}
                              className="block text-center text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                              View all results
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </form>
              </div>
            </div>

            {/* Wishlist */}
            <Link to="/profile?tab=wishlist" className="p-1.5 sm:p-2 text-gray-700 hover:text-black relative">
              <FiHeart size={20} />
            </Link>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="p-1.5 sm:p-2 text-gray-700 hover:text-black"
              >
                <FiUser size={20} />
              </button>

              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                  {isAuthenticated ? (
                    <>
                      <div className="px-4 py-2 text-sm text-gray-700 border-b">
                        Welcome, {user?.name || user?.email}
                      </div>
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        My Profile
                      </Link>
                      <Link
                        to="/orders"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        My Orders
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        Login
                      </Link>
                      <Link
                        to="/register"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        Register
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Cart */}
            <Link to="/cart" className="p-1.5 sm:p-2 -mr-1 sm:mr-0 text-gray-700 hover:text-black relative">
              <FiShoppingCart size={20} />
              {getTotalItems() > 0 && (
                <span className="absolute top-0 right-0 bg-black text-white text-[10px] leading-none rounded-full h-4 w-4 flex items-center justify-center">
                  {getTotalItems()}
                </span>
              )}
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="lg:hidden border-t border-gray-200">
          <div className="px-4 py-2 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-black hover:bg-gray-50 rounded-md"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header; 